import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Rate, RoomType } from 'pages/Settings/redux';
import { RootState } from 'redux/store';

export type ReserveDates = {
  start?: Date,
  end?: Date
}

/** Auto rate pick — replaces picking individual rate plans in the UI. */
export type RateStrategy = 'best' | 'mid' | 'highest';

/** Meal preference chips for bulk rate pruning (exact set match). */
export type MealPref = 'Breakfast' | 'Lunch' | 'Dinner';

export const getStayNights = (start?: Date, end?: Date) => {
  if (!start || !end) return 0;
  return Math.max(0, Math.round((+end - +start) / (24 * 60 * 60 * 1000)));
};

type SelectedBookRoom = {
  roomType: RoomType | undefined,
  ratePlan: Rate | undefined,
  amount: number,
  isNew: boolean,
}

export interface AppState {
  reserveDates: ReserveDates,
  selectedBookRooms: SelectedBookRoom[],
  roomsNeeded: number,
  rateStrategy: RateStrategy,
  /** Prefer rooms on the same / nearby floors when filling B rooms */
  preferSameFloor: boolean,
  /** Exact meal-set filter; empty + !mealRoomOnly = any meals */
  mealPrefs: MealPref[],
  /** When true, only room-only (no meal) rate plans */
  mealRoomOnly: boolean,
  /** Min guests on the rate; null = occupancy matching off */
  occupancyPref: number | null,
}

const initialState: AppState = {
  reserveDates: {},
  selectedBookRooms: [],
  roomsNeeded: 1,
  rateStrategy: 'best',
  preferSameFloor: true,
  mealPrefs: [],
  mealRoomOnly: false,
  occupancyPref: null,
};

export const counterSlice = createSlice({
  name: 'reserveRooms',
  initialState,
  reducers: {
    setReserveDates: (state, action: PayloadAction<ReserveDates>) => {
      state.reserveDates = action.payload;
    },
    setSelectedBookRooms: (state, action: PayloadAction<SelectedBookRoom[]>) => {
      state.selectedBookRooms = action.payload;
    },
    setRoomsNeeded: (state, action: PayloadAction<number>) => {
      // Clamp to a sane positive integer for booking search
      const n = Math.max(1, Math.min(999, Math.floor(Number(action.payload) || 1)));
      state.roomsNeeded = n;
    },
    setRateStrategy: (state, action: PayloadAction<RateStrategy>) => {
      state.rateStrategy = action.payload;
    },
    setPreferSameFloor: (state, action: PayloadAction<boolean>) => {
      state.preferSameFloor = action.payload;
    },
    setMealPrefs: (state, action: PayloadAction<MealPref[]>) => {
      state.mealPrefs = action.payload;
      // Meal chips and room-only are mutually exclusive
      if (action.payload.length) state.mealRoomOnly = false;
    },
    setMealRoomOnly: (state, action: PayloadAction<boolean>) => {
      state.mealRoomOnly = action.payload;
      if (action.payload) state.mealPrefs = [];
    },
    setOccupancyPref: (state, action: PayloadAction<number | null>) => {
      const n = action.payload;
      if (n == null) {
        state.occupancyPref = null;
        return;
      }
      state.occupancyPref = Math.max(1, Math.floor(Number(n) || 1));
    },
  },
});

export const {
  setReserveDates,
  setSelectedBookRooms,
  setRoomsNeeded,
  setRateStrategy,
  setPreferSameFloor,
  setMealPrefs,
  setMealRoomOnly,
  setOccupancyPref,
} = counterSlice.actions;

export const selectReserveDates = (state: RootState) => state.reserveRooms.reserveDates;
export const selectSelectedBookRooms = (state: RootState) => state.reserveRooms.selectedBookRooms;
export const selectRoomsNeeded = (state: RootState) => state.reserveRooms.roomsNeeded;
export const selectRateStrategy = (state: RootState) => state.reserveRooms.rateStrategy;
export const selectPreferSameFloor = (state: RootState) => state.reserveRooms.preferSameFloor;
export const selectMealPrefs = (state: RootState) => state.reserveRooms.mealPrefs;
export const selectMealRoomOnly = (state: RootState) => state.reserveRooms.mealRoomOnly;
export const selectOccupancyPref = (state: RootState) => state.reserveRooms.occupancyPref;

export default counterSlice.reducer;
